import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Check, Circle } from 'lucide-react';
import { C } from '../theme';
import planiqLogo from '../assets/logo.png';

const API = process.env.REACT_APP_API_URL || '';

const inputStyle = {
  width: '100%', padding: '10px 12px',
  background: 'rgba(255,255,255,0.8)', border: '1px solid #c8d8e8',
  borderRadius: 8, color: '#1a2a3a', fontSize: 14,
  outline: 'none', boxSizing: 'border-box', transition: 'border .15s',
};

const labelStyle = {
  display: 'block', color: '#6a8aaa', fontSize: 11,
  fontWeight: 600, textTransform: 'uppercase',
  letterSpacing: '0.08em', marginBottom: 6,
};

function StrengthChecklist({ password }) {
  const rules = [
    { label: 'At least 8 characters', ok: password.length >= 8 },
    { label: 'One uppercase letter', ok: /[A-Z]/.test(password) },
    { label: 'One number', ok: /[0-9]/.test(password) },
    { label: 'One special character', ok: /[!@#$%^&*(),.?":{}|<>]/.test(password) },
  ];
  return (
    <div style={{ marginTop: 8, marginBottom: 4 }}>
      {rules.map(r => (
        <div key={r.label} style={{ display: 'flex', alignItems: 'center', gap: 6, marginBottom: 4 }}>
          {r.ok
            ? <Check size={13} color="#2a8a5a" strokeWidth={2.5} />
            : <Circle size={13} color="#c0d0e0" strokeWidth={2} />}
          <span style={{ fontSize: 12, color: r.ok ? '#2a8a5a' : '#8aa0b8' }}>{r.label}</span>
        </div>
      ))}
    </div>
  );
}

export default function ResetPasswordPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  async function handleSubmit(e) {
    e.preventDefault();
    setError('');
    if (password !== confirm) { setError('Passwords do not match.'); return; }
    setLoading(true);
    try {
      const res = await fetch(`${API}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, new_password: password }),
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.detail || 'Reset failed');
      setDone(true);
      setTimeout(() => navigate('/login', { state: { message: 'Password updated. Please log in.' } }), 2000);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }

  const bg = {
    minHeight: '100vh', display: 'flex', alignItems: 'center',
    justifyContent: 'center', padding: 20,
    background: 'linear-gradient(135deg, #e8eef5 0%, #dce6f0 40%, #e4ecf5 70%, #d8e8f2 100%)',
    position: 'relative', overflow: 'hidden',
  };

  return (
    <div style={bg}>
      <div style={{ position: 'absolute', inset: 0, overflow: 'hidden', pointerEvents: 'none' }}>
        <div style={{ position: 'absolute', bottom: '8%', left: '8%', width: 120, height: 120, background: 'linear-gradient(135deg, #fff 60%, #c8d8e8)', borderRadius: 18, boxShadow: '8px 8px 32px rgba(100,140,180,0.18)', transform: 'rotate(-12deg)' }}/>
        <div style={{ position: 'absolute', bottom: '38%', left: '14%', width: 44, height: 44, background: 'linear-gradient(135deg, #e8f0f8, #d0e0ee)', borderRadius: 8, transform: 'rotate(-8deg)' }}/>
        {[[85,20],[92,35],[78,45],[88,58],[95,70],[80,72],[90,82]].map(([l,t],i)=>(
          <div key={i} style={{ position: 'absolute', left: l+'%', top: t+'%', width: 6, height: 6, borderRadius: '50%', background: 'rgba(150,180,210,0.5)' }}/>
        ))}
      </div>

      <div style={{ width: '100%', maxWidth: 400, position: 'relative', zIndex: 1 }}>
        <div style={{ textAlign: 'center', marginBottom: 36 }}>
          <div style={{ marginBottom: 10 }}>
            <img src={planiqLogo} alt="PlanIQ" style={{ width: 240, height: 'auto' }} />
          </div>
          <div style={{ fontSize: 13, color: '#8aa0b8', fontFamily: 'IBM Plex Mono, monospace' }}>planiq.live</div>
        </div>

        <div style={{
          background: 'rgba(255,255,255,0.75)', backdropFilter: 'blur(12px)',
          border: '1px solid rgba(255,255,255,0.9)', borderRadius: 16,
          padding: 36, boxShadow: '0 8px 40px rgba(80,120,160,0.12)',
        }}>
          {done ? (
            <div style={{ textAlign: 'center', padding: '8px 0' }}>
              <Check size={40} color="#2a8a5a" strokeWidth={1.5} style={{ marginBottom: 12 }}/>
              <div style={{ fontSize: 18, fontWeight: 700, color: '#1a2a3a', marginBottom: 8 }}>Password updated!</div>
              <div style={{ fontSize: 13, color: '#4a6a8a' }}>Redirecting to login…</div>
            </div>
          ) : (
            <>
              <div style={{ fontSize: 16, fontWeight: 700, color: '#1a2a3a', marginBottom: 20 }}>Choose a new password</div>
              {!token && (
                <div style={C.feedback('danger')}>No reset token found. Please use the link from your email.</div>
              )}
              <form onSubmit={handleSubmit}>
                <div style={{ marginBottom: 16 }}>
                  <label style={labelStyle}>New password</label>
                  <input type="password" value={password} onChange={e => setPassword(e.target.value)} required
                    style={inputStyle}
                    onFocus={e => e.target.style.borderColor = '#0090d0'}
                    onBlur={e => e.target.style.borderColor = '#c8d8e8'}
                  />
                  {password && <StrengthChecklist password={password} />}
                </div>
                <div style={{ marginBottom: 20 }}>
                  <label style={labelStyle}>Confirm password</label>
                  <input type="password" value={confirm} onChange={e => setConfirm(e.target.value)} required
                    style={inputStyle}
                    onFocus={e => e.target.style.borderColor = '#0090d0'}
                    onBlur={e => e.target.style.borderColor = '#c8d8e8'}
                  />
                </div>
                {error && <div style={C.feedback('danger')}>{error}</div>}
                <button type="submit" disabled={loading || !token} style={{
                  width: '100%', padding: '12px',
                  background: (loading || !token) ? '#c0d0e0' : 'linear-gradient(135deg, #0090d0, #0060a0)',
                  border: 'none', borderRadius: 8, color: '#fff',
                  fontWeight: 700, fontSize: 14, cursor: (loading || !token) ? 'not-allowed' : 'pointer',
                  boxShadow: '0 4px 16px rgba(0,120,200,0.25)', transition: 'all .15s',
                }}>
                  {loading ? 'Updating…' : 'Set New Password'}
                </button>
              </form>
              <div style={{ textAlign: 'center', marginTop: 18, fontSize: 13 }}>
                <span onClick={() => navigate('/login')} style={{ color: '#0090d0', fontWeight: 600, cursor: 'pointer' }}>Back to login</span>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
}
